import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Text,
  useClipboard,
} from "@chakra-ui/react";
import React from "react";
import { RiCheckLine, RiFileCopyLine, RiKey2Line } from "react-icons/ri";
import { useAvelaToast } from "src/hooks/useAvelaToast";

export type ApiCredentialSecretRevealProps = {
  clientName: string;
  clientId: string | null | undefined;
  clientSecret: string | null | undefined;
  userGroup?: string;
  teamName?: string;
  allowedIps?: string[];
  notice?: string;
};

const displayValue = (value: string | null | undefined) =>
  value && value.length > 0 ? value : "-";

const Field: React.FC<{
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}> = ({ label, value, mono }) => (
  <Flex direction="column" gap={1} flex={1} minW={0}>
    <FormLabel mb={0}>{label}</FormLabel>
    <Text fontFamily={mono ? "mono" : undefined} wordBreak="break-all">
      {value}
    </Text>
  </Flex>
);

const CopyableField: React.FC<{
  label: string;
  value: string;
}> = ({ label, value }) => {
  const toast = useAvelaToast();
  const { hasCopied, onCopy } = useClipboard(value, { timeout: 2000 });

  return (
    <Flex direction="column" gap={1} flex={1} minW={0}>
      <FormLabel mb={0}>{label}</FormLabel>
      <Flex alignItems="center" gap={1} minW={0}>
        <Text fontFamily="mono" wordBreak="break-all">
          {value}
        </Text>
        <IconButton
          aria-label={`Copy ${label}`}
          icon={<Icon as={hasCopied ? RiCheckLine : RiFileCopyLine} />}
          size="sm"
          variant="ghost"
          colorScheme={hasCopied ? "green" : "gray"}
          flexShrink={0}
          onClick={() => {
            onCopy();
            toast({
              title: `${label} copied`,
              status: "success",
              duration: 2000,
              isClosable: true,
            });
          }}
        />
      </Flex>
    </Flex>
  );
};

export const ApiCredentialSecretReveal: React.FC<
  ApiCredentialSecretRevealProps
> = ({
  clientName,
  clientId,
  clientSecret,
  userGroup,
  teamName,
  allowedIps,
  notice = "Client secrets will only show once here. Please securely save the information below.",
}) => {
  return (
    <Flex direction="column" gap={6}>
      <Alert status="warning" variant="subtle" borderRadius={6}>
        <AlertIcon alignSelf="flex-start" />
        <AlertDescription fontWeight="500">{notice}</AlertDescription>
      </Alert>

      <Flex direction="column" gap={2}>
        <Flex gap={6} direction={{ base: "column", md: "row" }}>
          <Field label="API Client Name" value={clientName} />
          <Field label="Role" value={displayValue(userGroup)} />
        </Flex>
        <Field label="Team" value={displayValue(teamName)} />
        <Field
          label="Allowed IPs"
          value={
            allowedIps && allowedIps.length > 0 ? allowedIps.join(", ") : "-"
          }
        />
      </Flex>

      <Box
        borderWidth="2px"
        borderColor="orange.300"
        borderRadius="md"
        bg="orange.50"
        p={5}
      >
        <Flex direction="column" gap={2}>
          <Flex alignItems="center" gap={2} mb={2}>
            <Icon as={RiKey2Line} boxSize={5} color="gray.800" />
            <Heading as="h2" size="sm" color="gray.800">
              Secrets (shown once)
            </Heading>
          </Flex>
          {clientId ? (
            <CopyableField label="Client ID" value={clientId} />
          ) : null}
          {clientSecret ? (
            <CopyableField label="Client Secret" value={clientSecret} />
          ) : null}
        </Flex>
      </Box>
    </Flex>
  );
};
